import {Component, ChangeDetectionStrategy} from '@angular/core';
import {Dialog} from '../../../../../interfaces/dialog/dialog';
import {DialogParams} from '../../../../../interfaces/dialog/dialog-params';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {OfferGoal} from '../../../../../models/offer-goal';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {OfferGoalService} from '../../../../../services/offer-goal/offer-goal.service';
import {DialogService} from '../../../../../services/dialog/dialog.service';

import {isNumber} from 'chart.js/helpers';
import {GoalTypeService} from '../../../../../services/goal-type/goal-type.service';
import {GoalType} from '../../../../../models/goal-type';
import {OfferGoalType} from '../../../../../models/offer-goal-type';
import {OfferGoalTypeService} from '../../../../../services/offer-goal-type/offer-goal-type.service';
import {concatMap, forkJoin, map, Observable, of} from 'rxjs';
import {IsGranted} from '../../../../../decorator/is-granted';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';

@Component({
  selector: 'app-offer-goal-form',
  imports: [
    ReactiveFormsModule,
    InputSwitchComponent,
    FormsModule,
    ConfirmDirective
],
  templateUrl: './offer-goal-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './offer-goal-form.component.scss'
})
@IsGranted(['ROLE_API_OFFER_READ'])
export class OfferGoalFormComponent implements Dialog {
  loaded = false;
  offerGoal: OfferGoal | null = null;
  goalTypes: GoalType[] = [];
  goalType: GoalType | null = null;
  toDelete: number[] = [];

  form = new FormGroup({
    private: new FormControl(false),
    multiple: new FormControl(false),
    minAge: new FormControl<number | null>(null),
    payoutAmount: new FormControl<number | null>(null),
    payoutPercent: new FormControl<number | null>(null),
    types: new FormArray([])
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private dialog: DialogService,
    private offerGoalService: OfferGoalService,
    private goalTypeService: GoalTypeService,
    private offerGoalTypeService: OfferGoalTypeService
  ) {
  }

  onDialogInit(params: DialogParams): void {
    this.loader.startLoading();

    this.offerGoal = params.data.offerGoal;
    const values: any = structuredClone(this.offerGoal);

    if (values.minAge < 1) {
      values.minAge = null;
    }

    if (values.payoutAmount < 0.01) {
      values.payoutAmount = null;
    }

    if (values.payoutPercent) {
      values.payoutPercent *= 100.0;
    }

    values?.types.forEach((type: OfferGoalType) => {
      if (type.minAge !== null && type.minAge < 1) {
        type.minAge = null;
      }

      if (type.payoutAmount !== null && type.payoutAmount < 0.01) {
        type.payoutAmount = null;
      }

      if (type.payoutPercent) {
        type.payoutPercent *= 100.0;
      }

      this.addFormGoalType(type);
    });

    this.toDelete = [];

    this.form.patchValue(values);

    this.goalTypeService.findAll({goalId: this.offerGoal?.goal?.id, sort: 'name'}).subscribe((goalTypes: GoalType[]) => {
      this.goalTypes = goalTypes;

      this.loader.stopLoading();
      this.loaded = true;
    });
  }

  getFormGoalTypes(): FormArray {
    return this.form.get('types') as FormArray;
  }

  getFormGoalTypeControls(): FormGroup[] {
    return this.getFormGoalTypes().controls as FormGroup[];
  }

  addFormGoalType(type: OfferGoalType): void {
    const formTypes = this.getFormGoalTypes();

    const formGroup = new FormGroup({
      id: new FormControl(type.id),
      goalTypeId: new FormControl(type.goalType?.id ?? null),
      name: new FormControl(type.goalType?.name),
      minAge: new FormControl<number | null>(null),
      payoutAmount: new FormControl<number | null>(null),
      payoutPercent: new FormControl<number | null>(null),
    });

    formTypes.push(formGroup);

    formGroup.patchValue({
      minAge: type.minAge,
      payoutAmount: type.payoutAmount,
      payoutPercent: type.payoutPercent,
    });
  }

  onSubmit(): void {
    if (!this.form.valid || !this.offerGoal?.id) {
      return;
    }

    this.loader.startLoading();

    let params: any = {};

    params.private = !!this.form.get('private')?.value;
    params.multiple = !!this.form.get('multiple')?.value;
    params.minAge = this.getMinAge(this.form.get('minAge')?.value);
    params.payoutAmount = this.getPayoutAmount(this.form.get('payoutAmount')?.value);
    params.payoutPercent = this.getPayoutPercent(this.form.get('payoutPercent')?.value);

    this.offerGoalService.update(this.offerGoal.id, params).subscribe(() => {
      this.updateGoalTypes().subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Goal has been updated.');
        this.dialog.closeDialog();
      });
    });
  }

  getMinAge(value: any): number | null {
    if (!isNumber(value) || value < 1) {
      return null;
    }

    return Math.round(value);
  }

  getPayoutAmount(value: any): number | null {
    if (!isNumber(value) || value < 0.01) {
      return null;
    }

    return value;
  }

  getPayoutPercent(value: any): number | null {
    if (isNumber(value)) {
      return value / 100.0;
    } else {
      return null;
    }
  }

  getAvailableGoalTypes(): GoalType[] {
    const usedIds = this.getFormGoalTypeControls().map((formGroup: FormGroup) => formGroup.get('goalTypeId')?.value);

    return this.goalTypes.filter((type: GoalType) => !usedIds.includes(type.id));
  }

  addGoalType(): void {
    if (this.goalType && this.getAvailableGoalTypes().includes(this.goalType)) {
      const offerGoalType = new OfferGoalType();
      offerGoalType.goalType = this.goalType;
      this.addFormGoalType(offerGoalType);
    }
    this.goalType = null;
  }

  deleteOfferType(formGroup: FormGroup): void {
    const i = this.getFormGoalTypeControls().indexOf(formGroup);
    if (i >= 0) {
      this.getFormGoalTypes().removeAt(i);
    }

    const id = formGroup.get('id')?.value ?? null;
    if (id) {
      this.toDelete.push(id);
    }
  }

  updateGoalTypes(): Observable<void> {
    const deleteRequests: Observable<void>[] = [];

    this.toDelete.forEach((id: number) => {
      if (id) {
        deleteRequests.push(this.offerGoalTypeService.delete(id));
      }
    });

    const saveRequests: Observable<void>[] = [];
    const goalTypes: any[] = this.form.get('types')?.value as any[];

    goalTypes.forEach((type: any) => {
      if (type.id) {
        const updateParams = {
          minAge: this.getMinAge(type.minAge),
          payoutAmount: this.getPayoutAmount(type.payoutAmount),
          payoutPercent: this.getPayoutPercent(type.payoutPercent),
        };
        saveRequests.push(this.offerGoalTypeService.update(type.id, updateParams));
      } else {
        const createParams = {
          offerGoalId: this.offerGoal?.id,
          goalTypeId: type.goalTypeId,
          minAge: this.getMinAge(type.minAge),
          payoutAmount: this.getPayoutAmount(type.payoutAmount),
          payoutPercent: this.getPayoutPercent(type.payoutPercent),
        };
        saveRequests.push(this.offerGoalTypeService.create(createParams));
      }
    });

    return this.runRequests(deleteRequests).pipe(
      concatMap(() => this.runRequests(saveRequests))
    );
  }

  private runRequests(requests: Observable<void>[]): Observable<void> {
    if (!requests.length) {
      return of(undefined);
    }

    return forkJoin(requests).pipe(map(() => undefined));
  }
}
